import { useMemo } from "react";
import { useAvailableFilterFields } from "@/hooks/use-available-filter-fields";
import {
  type FilterClearingInfo,
  type FilterColumn,
  type FilterFieldDefinition,
  processMetricCardFilters,
} from "@/lib/filter-display-utils";
import { createFilterClearHandler } from "@/lib/filter-state-utils";
import type { FilterGroup } from "@/lib/filters";

interface FilterState {
  accountFilters: FilterGroup;
  opportunityFilters: FilterGroup;
  touchpointFilters: FilterGroup;
  campaignFilters?: FilterGroup;
  hasAccountFilters: boolean;
  hasOpportunityFilters: boolean;
  hasTouchpointFilters: boolean;
  hasCampaignFilters?: boolean;
  setAccountFilters: (filters: FilterGroup) => void;
  setOpportunityFilters: (filters: FilterGroup) => void;
  setTouchpointFilters: (filters: FilterGroup) => void;
  setCampaignFilters?: (filters: FilterGroup) => void;
  clearAllFilters: () => void;
}

interface UseMetricCardFiltersResult {
  displayFilters: Record<string, unknown>;
  filterColumns: FilterColumn[];
  totalFilterCount: number;
  hasActiveFilters: boolean;
  getTotalFilterCount: () => number;
  handleClearFilter: (key: string, clearingInfo?: FilterClearingInfo) => void;
  clearAllFilters: () => void;
}

/**
 * Custom hook that encapsulates all metric card filter processing logic
 * Eliminates repetitive code across campaign, channel, salesperson, and webpage metric cards
 */
export function useMetricCardFilters(
  filterState: FilterState,
): UseMetricCardFiltersResult {
  const {
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    campaignFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    hasCampaignFilters,
    setAccountFilters,
    setOpportunityFilters,
    setTouchpointFilters,
    setCampaignFilters,
    clearAllFilters,
  } = filterState;

  // Get field definitions for human-readable names
  const { fields: accountFields = [] } = useAvailableFilterFields("account");
  const { fields: opportunityFields = [] } =
    useAvailableFilterFields("opportunity");
  const { fields: campaignFields = [] } = useAvailableFilterFields("campaign");
  // Touchpoint fields are not available through useAvailableFilterFields API
  const touchpointFields = useMemo((): FilterFieldDefinition[] => [], []);

  // Process filters for display using utility function
  const { displayFilters, filterColumns, totalFilterCount } = useMemo(() => {
    return processMetricCardFilters(
      accountFilters,
      opportunityFilters,
      touchpointFilters,
      hasAccountFilters,
      hasOpportunityFilters,
      hasTouchpointFilters,
      accountFields,
      opportunityFields,
      touchpointFields,
      campaignFilters,
      hasCampaignFilters,
      campaignFields,
    );
  }, [
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    campaignFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    hasCampaignFilters,
    accountFields,
    opportunityFields,
    touchpointFields,
    campaignFields,
  ]);

  // Check if any filters are active
  const hasActiveFilters =
    hasAccountFilters ||
    hasOpportunityFilters ||
    hasTouchpointFilters ||
    !!hasCampaignFilters;

  // Handle clearing individual filters using utility function
  const handleClearFilter = useMemo(() => {
    return createFilterClearHandler(
      accountFilters,
      opportunityFilters,
      touchpointFilters,
      setAccountFilters,
      setOpportunityFilters,
      setTouchpointFilters,
      campaignFilters,
      setCampaignFilters,
    );
  }, [
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    campaignFilters,
    setAccountFilters,
    setOpportunityFilters,
    setTouchpointFilters,
    setCampaignFilters,
  ]);

  // Calculate total filter count
  const getTotalFilterCount = useMemo(
    () => () => totalFilterCount,
    [totalFilterCount],
  );

  return {
    displayFilters,
    filterColumns,
    totalFilterCount,
    hasActiveFilters,
    getTotalFilterCount,
    handleClearFilter,
    clearAllFilters,
  };
}
